/**
 * API методы для работы с шаблонами сообщений
 * Дата: 28.12.2025 - Создан для TODO-013 (Templates Page)
 * Дата: 30.12.2025 - Добавлен getWABATemplates для TODO-027 (WABA Template Selector)
 * Связан с: internal/handler/template.go, frontend/src/hooks/useTemplates.ts, frontend/src/pages/Templates.tsx
 */
import { apiClient } from './client';
export { SEND_SCHEDULE_TYPES, TEMPLATE_CATEGORIES } from '@/features/templates/templateTaxonomy';
import type { OkdocScenarioProfile } from '@/features/okdoc/types';

// Дата: 07.02.2026 - TemplateButton для TG Bot inline keyboard
export interface TemplateButton {
  text: string;          // Текст кнопки: "👍 Отлично"
  callback_data: string; // Для TG: "feedback_good"
  type: 'quick_reply' | 'url' | 'phone_number';
  url?: string;         // Для кнопок типа URL
}

// Дата: 09.02.2026 - TODO-060: Расписание отправки шаблона
export interface SendSchedule {
  type: 'event_triggered' | 'daily_scheduled' | 'manual' | '';
  send_time?: string;                  // "10:00" (для daily_scheduled)
  timezone?: string;                   // "Europe/Moscow"
  stagger_enabled?: boolean;           // Растягивать ли пакетную рассылку
  stagger_window_minutes?: number;     // Окно растягивания (минуты)
  delay_minutes?: number;              // Задержка после события (event_triggered)
  fallback_enabled?: boolean;          // Использовать FallbackEngine
  max_per_batch?: number;              // Макс. сообщений за batch
}

export interface Template {
  id: string;
  tenant_id: string;
  name: string;
  content: string;
  variables: string[];
  template_type: string; // Технический тип: text | waba_template. Не равен feedback context / auto-reply context.
  channel?: string;
  messenger?: string;
  // Дата: 02.02.2026 - Добавлена категория шаблона
  // Дата: 26.02.2026 - PLAN Manual Broadcasts: добавлена категория manual
  category?: 'notification' | 'service' | 'marketing' | 'feedback' | 'birthday' | 'manual';
  is_active: boolean;
  external_template_id?: string; // GUID Meta/Wazzup template для реальной WABA отправки
  // Дата: 30.12.2025 - Добавлено для TODO-027 (сохранение маппинга переменных WABA)
  waba_variables_mapping?: Record<string, WABAVarMapping>;
  // Дата: 07.02.2026 - Добавлено для TODO-053 (Template Buttons ↔ Auto-Replies)
  buttons?: TemplateButton[];      // Кнопки для TG Bot inline keyboard
  text_alternative?: string;      // Текстовая альтернатива для каналов без кнопок
  // Дата: 09.02.2026 - TODO-060: Расписание отправки + rate limiting
  send_schedule?: SendSchedule;   // Расписание отправки шаблона
  okdoc_scenario_profile?: OkdocScenarioProfile;
  created_at: string;
  updated_at: string;
}

// Маппинг переменной WABA шаблона
export interface WABAVarMapping {
  type: 'static' | 'dynamic';
  value?: string;  // Для static
  field?: string;  // Для dynamic: firstname_middlename, phone, etc.
}

export interface TemplateVariable {
  id: string;
  code: string;
  name: string;
  description: string;
  example: string;
  category: string;
  data_source: string;
  is_required: boolean;
}

export interface EventTemplateBindingSlot {
  channel?: string;
  label: string;
  has_binding: boolean;
  mapping_active: boolean;
  warning?: string;
  runtime?: EventTemplateRuntimeBinding;
  template?: {
    id: string;
    name: string;
    type: string;
    category?: string;
    is_active: boolean;
    priority: number;
    delay_hours: number;
  };
}

export interface EventTemplateRuntimeBinding {
  has_template: boolean;
  source_kind?: 'dedicated_override' | 'generic_binding' | 'legacy_fallback' | 'none';
  source_event_type?: string;
  source_event_label?: string;
  source_channel?: string;
  source_channel_label?: string;
  source_description?: string;
  template?: {
    id: string;
    name: string;
    type: string;
    category?: string;
    is_active: boolean;
    priority: number;
    delay_hours: number;
  };
}

export interface EventTemplateBindingItem {
  event_type: string;
  label: string;
  dispatch_mode?: 'auto' | 'manual';
  generic_binding: EventTemplateBindingSlot;
  channel_bindings: EventTemplateBindingSlot[];
}

export interface EventTemplateBindingsResponse {
  event_types: Array<{ event_type: string; label: string }>;
  channels: Array<{ channel: string; label: string }>;
  bindings: EventTemplateBindingItem[];
  total: number;
  cluster_followup_dispatch_mode?: 'auto' | 'manual';
}

const DEFAULT_EVENT_BINDING_CHANNELS: Array<{ channel: string; label: string }> = [
  { channel: 'whatsapp_waba', label: 'WhatsApp WABA' },
  { channel: 'whatsapp_regular', label: 'WhatsApp Regular' },
  { channel: 'telegram', label: 'Telegram Bot' },
  { channel: 'telegram_client', label: 'Telegram Client' },
  { channel: 'max', label: 'MAX' },
  { channel: 'sms', label: 'SMS' },
];

function normalizeBindingSlot(slot?: Partial<EventTemplateBindingSlot>, fallback?: Partial<EventTemplateBindingSlot>): EventTemplateBindingSlot {
  return {
    channel: slot?.channel ?? fallback?.channel,
    label: slot?.label || fallback?.label || '',
    has_binding: slot?.has_binding ?? fallback?.has_binding ?? false,
    mapping_active: slot?.mapping_active ?? fallback?.mapping_active ?? false,
    warning: slot?.warning ?? fallback?.warning,
    runtime: slot?.runtime ?? fallback?.runtime,
    template: slot?.template ?? fallback?.template,
  };
}

function normalizeEventBindingsResponse(data: any): EventTemplateBindingsResponse {
  const channels = Array.isArray(data?.channels) && data.channels.length > 0
    ? data.channels
    : DEFAULT_EVENT_BINDING_CHANNELS;

  const bindings = Array.isArray(data?.bindings) ? data.bindings.map((binding: any) => {
    const genericBinding = binding?.generic_binding
      ? normalizeBindingSlot(binding.generic_binding, { label: 'Общая generic-привязка' })
      : normalizeBindingSlot({
          label: 'Общая generic-привязка',
          has_binding: binding?.has_binding,
          mapping_active: binding?.mapping_active,
          warning: binding?.warning,
          template: binding?.template,
        });

    const channelBindings = Array.isArray(binding?.channel_bindings)
      ? binding.channel_bindings.map((slot: any) => normalizeBindingSlot(slot))
      : channels.map((channel: { channel: string; label: string }) => normalizeBindingSlot({
          channel: channel.channel,
          label: channel.label,
          warning: 'Будет использована generic-привязка',
        }));

    return {
      event_type: binding?.event_type || '',
      label: binding?.label || binding?.event_type || '',
      dispatch_mode: binding?.dispatch_mode === 'auto' || binding?.dispatch_mode === 'manual'
        ? binding.dispatch_mode
        : undefined,
      generic_binding: genericBinding,
      channel_bindings: channelBindings,
    } satisfies EventTemplateBindingItem;
  }) : [];

  return {
    event_types: Array.isArray(data?.event_types) ? data.event_types : [],
    channels,
    bindings,
    total: typeof data?.total === 'number' ? data.total : bindings.length,
    cluster_followup_dispatch_mode:
      data?.cluster_followup_dispatch_mode === 'auto' || data?.cluster_followup_dispatch_mode === 'manual'
        ? data.cluster_followup_dispatch_mode
        : undefined,
  };
}

export interface TemplateListParams {
  tenantId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export const templatesApi = {
  // SuperAdmin: передаёт tenant_id; Employee: backend берёт из context
  list: async (tenantIdOrParams?: string | TemplateListParams): Promise<{ templates: Template[]; total: number }> => {
    const params: Record<string, string> = {};
    const options = typeof tenantIdOrParams === 'string' ? { tenantId: tenantIdOrParams } : tenantIdOrParams || {};
    const tenantId = options.tenantId;
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    if (options.search?.trim()) {
      params.search = options.search.trim();
    }
    // 09.10.26. Не удалять по требованию владельца системы.
    // Связано с Pagination.GetLimit: значение >200 сервер подменяет на 50.
    const requestedLimit = options.limit ?? 200;
    params.limit = String(Math.min(Math.max(requestedLimit, 1), 200));
    params.offset = String(options.offset ?? 0);
    const response = await apiClient.get('/templates', { params });
    return response.data;
  },

  get: async (id: string, tenantId?: string): Promise<Template> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.get(`/templates/${id}`, { params });
    return response.data;
  },

  // tenant_id обязателен для SuperAdmin, игнорируется для Employee
  create: async (data: Partial<Template> & { tenant_id?: string }): Promise<Template> => {
    const response = await apiClient.post('/templates', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Template>, tenantId?: string): Promise<Template> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.put(`/templates/${id}`, data, { params });
    return response.data;
  },

  delete: async (id: string, tenantId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    await apiClient.delete(`/templates/${id}`, { params });
  },

  getVariables: async (category?: string): Promise<{ variables: TemplateVariable[]; categories: string[] }> => {
    const params: Record<string, string> = {};
    if (category) {
      params.category = category;
    }
    const response = await apiClient.get('/template-variables', { params });
    return response.data;
  },

  preview: async (
    id: string,
    data: Record<string, string>,
    tenantId?: string,
    channel?: string,
  ): Promise<TemplatePreviewResponse> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.post(`/templates/${id}/preview`, { variables: data, channel }, { params });
    return response.data;
  },

  // WABA Templates (TODO-027)
  getWABATemplates: async (tenantId?: string): Promise<{ templates: WABATemplate[] }> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.get('/templates/waba', { params });
    return response.data;
  },

  // Дата: 26.01.2026 - Массовая активация/деактивация шаблонов
  bulkActivate: async (ids: string[], tenantId?: string): Promise<{ updated: number }> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.post('/templates/bulk-activate', { ids }, { params });
    return response.data;
  },

  bulkDeactivate: async (ids: string[], tenantId?: string): Promise<{ updated: number }> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.post('/templates/bulk-deactivate', { ids }, { params });
    return response.data;
  },

  listEventBindings: async (tenantId?: string): Promise<EventTemplateBindingsResponse> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.get('/templates/event-bindings', { params });
    return normalizeEventBindingsResponse(response.data);
  },

  setEventBinding: async (
    eventType: string,
    templateId: string,
    channel?: string,
    tenantId?: string,
    activateTemplate = false,
  ): Promise<{ message: string; warnings?: string[] }> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.post('/templates/event-bindings', {
      event_type: eventType,
      channel,
      template_id: templateId,
      activate_template: activateTemplate,
    }, { params });
    return response.data;
  },

  clearEventBinding: async (eventType: string, channel?: string, tenantId?: string): Promise<{ message: string }> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    if (channel) {
      params.channel = channel;
    }
    const response = await apiClient.delete(`/templates/event-bindings/${eventType}`, { params });
    return response.data;
  },
};

export interface WABATemplate {
  id: string;
  name: string;
  title?: string;
  language: string;
  category: string;
  status: string;
  template_guid: string;
  variables: string[]; // ["1", "2", "3"]
  body_text?: string;
  header_text?: string;
  footer_text?: string;
  components?: WABAComponent[];
  buttons?: WABAButton[];
}

export interface WABAComponent {
  type: string; // "HEADER", "BODY", "FOOTER", "BUTTONS"
  format?: string; // "TEXT", "IMAGE", "VIDEO"
  text?: string;
  example?: any;
  buttons?: WABAButton[];
}

export interface WABAButton {
  type: string; // "URL", "QUICK_REPLY", "PHONE_NUMBER"
  text: string;
  url?: string;
}

export interface WABAPreview {
  header_text?: string;
  body_text?: string;
  footer_text?: string;
  buttons?: WABAButton[];
}

export interface TemplatePreviewResponse {
  preview: string;
  variables_used: string[];
  variables_missing: string[];
  waba_preview?: WABAPreview | null;
  buttons?: TemplateButton[];
  action_url?: string;
}

